/**
 * Canonical image upload presets for the Othaim platform.
 * See docs/engineering/IMAGE_UPLOAD_STANDARD.md
 */

export type ImageUploadPreset = 'avatar' | 'cover' | 'banner' | 'product';

export type ImageUploadTranslator = (en: string, ar: string) => string;

/** Accepted MIME types for standard image uploads (JPG, PNG, WebP). */
export const IMAGE_UPLOAD_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export type ImageUploadMimeType = (typeof IMAGE_UPLOAD_MIME_TYPES)[number];

export const IMAGE_UPLOAD_MIME_SET = new Set<string>(IMAGE_UPLOAD_MIME_TYPES);

/** Value for `<input accept="…">` on standard image pickers. */
export const IMAGE_UPLOAD_ACCEPT = IMAGE_UPLOAD_MIME_TYPES.join(',');

/** Reject raw files larger than this before attempting canvas decode/compress. */
export const IMAGE_UPLOAD_PRE_COMPRESS_LIMIT_BYTES = 50 * 1024 * 1024;

/** Server-side absolute ceiling for image uploads (presign fallback). */
export const IMAGE_UPLOAD_SERVER_MAX_BYTES = 15 * 1024 * 1024;

export interface ImageUploadPresetConfig {
  /** Long-edge cap (width for landscape, height for portrait). */
  maxWidth: number;
  maxHeight: number;
  /** Target output size after client compression. */
  maxBytes: number;
  /** Initial Canvas encode quality (0–1). */
  quality: number;
  /** Minimum quality when iteratively shrinking to hit maxBytes. */
  minQuality: number;
}

export const IMAGE_UPLOAD_PRESETS: Record<ImageUploadPreset, ImageUploadPresetConfig> = {
  avatar: {
    maxWidth: 512,
    maxHeight: 512,
    maxBytes: 200 * 1024,
    quality: 0.82,
    minQuality: 0.5,
  },
  cover: {
    maxWidth: 1920,
    maxHeight: 1080,
    maxBytes: 500 * 1024,
    quality: 0.82,
    minQuality: 0.5,
  },
  banner: {
    maxWidth: 1920,
    maxHeight: 640,
    maxBytes: 500 * 1024,
    quality: 0.82,
    minQuality: 0.5,
  },
  product: {
    maxWidth: 1200,
    maxHeight: 1200,
    maxBytes: 400 * 1024,
    quality: 0.82,
    minQuality: 0.5,
  },
};

/** Preset size caps mirrored on the server presign endpoint. */
export const IMAGE_UPLOAD_PRESET_SERVER_CAPS: Record<ImageUploadPreset, number> = {
  avatar: IMAGE_UPLOAD_PRESETS.avatar.maxBytes,
  cover: IMAGE_UPLOAD_PRESETS.cover.maxBytes,
  banner: IMAGE_UPLOAD_PRESETS.banner.maxBytes,
  product: IMAGE_UPLOAD_PRESETS.product.maxBytes,
};

export function isImageUploadPreset(value: string): value is ImageUploadPreset {
  return value in IMAGE_UPLOAD_PRESETS;
}

export function getImageUploadPresetConfig(preset: ImageUploadPreset): ImageUploadPresetConfig {
  return IMAGE_UPLOAD_PRESETS[preset];
}

/**
 * Validates a file against the global image upload standard.
 * Returns a localized error string or null when acceptable.
 */
export function validateImageUploadFile(
  file: File,
  T: ImageUploadTranslator,
  opts: {
    preset?: ImageUploadPreset;
    maxPreCompressBytes?: number;
  } = {}
): string | null {
  const preCompressLimit = opts.maxPreCompressBytes ?? IMAGE_UPLOAD_PRE_COMPRESS_LIMIT_BYTES;

  if (!IMAGE_UPLOAD_MIME_SET.has(file.type)) {
    return T(
      `"${file.name}" is not a supported image type. Use JPEG, PNG, or WebP.`,
      `"${file.name}" ليس نوع صورة مدعومًا. استخدم JPEG أو PNG أو WebP.`
    );
  }

  if (file.size > preCompressLimit) {
    const mb = (file.size / 1024 / 1024).toFixed(0);
    const capMb = (preCompressLimit / 1024 / 1024).toFixed(0);
    return T(
      `"${file.name}" is too large (${mb} MB). Choose a smaller image (max ${capMb} MB before compression).`,
      `"${file.name}" كبير جداً (${mb} ميغابايت). اختر صورة أصغر (الحد الأقصى ${capMb} ميغابايت قبل الضغط).`
    );
  }

  return null;
}
